import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Building2, Search, X } from 'lucide-react'

import { api } from '@/api/client'
import type { Partner, PartnerGroup } from '@/types/models'
import { cn } from '@/lib/utils'

interface Props {
  value: number[]
  onChange: (ids: number[]) => void
  placeholder?: string
  bizClass?: 'customer' | 'vendor' | 'both' | ''
  /** true 면 상위 거래처(그룹)도 표시. 그룹 클릭 시 하위 파트너 전체 선택/해제. */
  showGroups?: boolean
}

export function MultiPartnerPicker({
  value, onChange, placeholder = '거래처 이름으로 검색...', bizClass = '',
  showGroups = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [labels, setLabels] = useState<Record<number, string>>({})
  const [groupChildren, setGroupChildren] = useState<Record<number, number[]>>({})
  const [activeIdx, setActiveIdx] = useState(0)
  // Shift+클릭 범위선택용 앵커(직전 클릭한 개별거래처 인덱스)
  const [lastIdx, setLastIdx] = useState<number | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // 선택된 파트너 라벨 lazy fetch (칩 표시용)
  useEffect(() => {
    const missing = value.filter((id) => !labels[id])
    if (!missing.length) return
    Promise.all(
      missing.map((id) => api<Partner>(`/api/partners/${id}`).catch(() => null)),
    ).then((ps) => {
      const next = { ...labels }
      for (const p of ps) if (p) next[p.id] = `${p.code} ${p.name}`
      setLabels(next)
    })
  }, [value, labels])

  // 바깥 클릭 시 드롭다운 닫기
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const { data: results = [] } = useQuery({
    queryKey: ['partners-picker', search, bizClass],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) { params.set('q', search); params.set('q_field', 'name') }
      if (bizClass) params.set('biz_class', bizClass)
      return api<Partner[]>(`/api/partners?${params}`)
    },
    enabled: open,
    staleTime: 30_000,
  })

  const { data: groups = [] } = useQuery({
    queryKey: ['partner-groups-picker', search],
    queryFn: () => {
      const params = new URLSearchParams()
      if (search) params.set('q', search)
      return api<PartnerGroup[]>(`/api/partner-groups?${params}`)
    },
    enabled: open && showGroups,
    staleTime: 30_000,
  })

  function toggle(p: Partner, idx?: number, shift?: boolean) {
    // Shift+클릭: 직전 클릭한 항목 ~ 현재 항목(검색결과 리스트 기준) 범위를 일괄 토글
    if (shift && idx != null && lastIdx != null && lastIdx !== idx) {
      const [a, b] = idx < lastIdx ? [idx, lastIdx] : [lastIdx, idx]
      const range = filteredResults.slice(a, b + 1)
      const check = !value.includes(p.id)
      const set = new Set(value)
      const nextLabels = { ...labels }
      for (const rp of range) {
        if (check) { set.add(rp.id); nextLabels[rp.id] = `${rp.code} ${rp.name}` }
        else set.delete(rp.id)
      }
      setLabels(nextLabels)
      onChange(Array.from(set))
      if (idx != null) setLastIdx(idx)
      return
    }
    if (value.includes(p.id)) {
      onChange(value.filter((x) => x !== p.id))
    } else {
      setLabels({ ...labels, [p.id]: `${p.code} ${p.name}` })
      onChange([...value, p.id])
    }
    if (idx != null) setLastIdx(idx)
  }

  // 검색결과(현재 필터된 개별거래처) 전체 선택/해제
  function selectAllFiltered() {
    const set = new Set(value)
    const nextLabels = { ...labels }
    for (const p of filteredResults) { set.add(p.id); nextLabels[p.id] = `${p.code} ${p.name}` }
    setLabels(nextLabels)
    onChange(Array.from(set))
  }
  function clearAllFiltered() {
    const ids = new Set(filteredResults.map((p) => p.id))
    onChange(value.filter((x) => !ids.has(x)))
  }

  async function toggleGroup(g: PartnerGroup) {
    let childIds = groupChildren[g.id]
    let childPartners: Partner[] = []
    if (!childIds) {
      const params = new URLSearchParams()
      params.set('group_id', String(g.id))
      if (bizClass) params.set('biz_class', bizClass)
      childPartners = await api<Partner[]>(`/api/partners?${params}`)
      childIds = childPartners.map((p) => p.id)
      setGroupChildren((cur) => ({ ...cur, [g.id]: childIds! }))
    }
    if (!childIds.length) return
    const allSelected = childIds.every((id) => value.includes(id))
    if (allSelected) {
      onChange(value.filter((id) => !childIds!.includes(id)))
    } else {
      if (childPartners.length) {
        setLabels((cur) => {
          const next = { ...cur }
          for (const p of childPartners) if (!next[p.id]) next[p.id] = `${p.code} ${p.name}`
          return next
        })
      }
      onChange(Array.from(new Set([...value, ...childIds])))
    }
  }

  function remove(id: number) { onChange(value.filter((x) => x !== id)) }

  const filteredResults = results  // 백엔드에서 이미 q 로 필터됨
  const hasAny = (showGroups && groups.length > 0) || filteredResults.length > 0
  const allFilteredSelected =
    filteredResults.length > 0 && filteredResults.every((p) => value.includes(p.id))

  // 키보드 네비게이션용 flat 리스트 (그룹 먼저, 파트너 다음)
  type NavItem =
    | { kind: 'group'; item: PartnerGroup }
    | { kind: 'partner'; item: Partner }
  const navItems: NavItem[] = [
    ...(showGroups ? groups.map((g) => ({ kind: 'group' as const, item: g })) : []),
    ...filteredResults.map((p) => ({ kind: 'partner' as const, item: p })),
  ]

  useEffect(() => { setActiveIdx(0); setLastIdx(null) }, [search, open])

  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-picker-idx="${activeIdx}"]`,
    )
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx, open, navItems.length])

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) setOpen(true)
      setActiveIdx((i) => Math.min(i + 1, Math.max(navItems.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home') {
      e.preventDefault(); setActiveIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault(); setActiveIdx(Math.max(navItems.length - 1, 0))
    } else if (e.key === 'Enter') {
      const it = navItems[activeIdx]
      if (it) {
        e.preventDefault()
        if (it.kind === 'group') toggleGroup(it.item)
        else toggle(it.item, activeIdx - (showGroups ? groups.length : 0))
      }
    } else if (e.key === 'Escape') {
      setOpen(false); inputRef.current?.blur()
    }
  }

  return (
    <div ref={rootRef} className="relative min-w-[220px] flex-1">
      {/* 입력창 + 카운트 배지 + X 아이콘 */}
      <div className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 border rounded-lg bg-white transition-colors',
        open ? 'border-emerald-500 ring-2 ring-emerald-100' : 'border-slate-300 hover:border-slate-400',
      )}>
        <Search className="w-3.5 h-3.5 text-slate-400 shrink-0"/>
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="flex-1 min-w-[80px] text-sm outline-none bg-transparent placeholder-slate-400"
        />
        {value.length > 0 && (
          <>
            <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 tabular-nums shrink-0">
              {value.length}건
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange([]); setSearch(''); inputRef.current?.focus() }}
              className="p-0.5 text-slate-400 hover:text-rose-500 shrink-0"
              title="선택 전체 해제"
            >
              <X className="w-3.5 h-3.5"/>
            </button>
          </>
        )}
      </div>

      {/* 선택 칩 (최대 6개 + 요약) */}
      {value.length > 1 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {value.slice(0, 6).map((id) => (
            <span key={id}
                  className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 rounded-full pl-2 pr-1 py-0.5 max-w-[220px]">
              <span className="truncate">{labels[id] || `#${id}`}</span>
              <button type="button" onClick={() => remove(id)}
                      className="hover:bg-emerald-200 rounded-full p-0.5 shrink-0">
                <X className="w-3 h-3"/>
              </button>
            </span>
          ))}
          {value.length > 6 && (
            <button type="button" onClick={() => { setOpen(true); inputRef.current?.focus() }}
                    className="inline-flex items-center text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-full px-2 py-0.5">
              +{value.length - 6} 더
            </button>
          )}
        </div>
      )}

      {/* 드롭다운 */}
      {open && (
        <div className="absolute z-40 mt-1 left-0 right-0 min-w-[320px] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          <div ref={listRef} className="max-h-80 overflow-y-auto divide-y divide-slate-100">
            {showGroups && groups.length > 0 && (
              <>
                <div className="px-3 py-1.5 bg-blue-50/70 text-[10px] font-semibold uppercase tracking-wider text-blue-800">
                  상위 거래처 ({groups.length})
                </div>
                {groups.map((g, gi) => {
                  const childIds = groupChildren[g.id]
                  const selectedCount = childIds ? childIds.filter((id) => value.includes(id)).length : 0
                  const allSelected = childIds && childIds.length > 0 && selectedCount === childIds.length
                  const navI = gi  // 그룹은 nav 리스트 앞부분
                  const active = navI === activeIdx
                  return (
                    <button key={`g-${g.id}`} type="button"
                            data-picker-idx={navI}
                            onMouseEnter={() => setActiveIdx(navI)}
                            onClick={() => toggleGroup(g)}
                            className={cn(
                              'w-full text-left px-3 py-2 text-sm flex items-center gap-2',
                              active
                                ? 'bg-blue-100'
                                : allSelected
                                  ? 'bg-blue-50'
                                  : 'hover:bg-blue-50',
                            )}>
                      <Building2 className="w-3.5 h-3.5 text-blue-500 shrink-0"/>
                      <span className="font-medium truncate">{g.name}</span>
                      <span className="ml-auto text-[11px] text-slate-500 tabular-nums shrink-0">
                        {childIds ? `${selectedCount}/${childIds.length} 선택` : `${g.member_count}개 자식`}
                      </span>
                    </button>
                  )
                })}
              </>
            )}
            {filteredResults.length > 0 && (
              <>
                <div className="sticky top-0 z-10 px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                    개별 거래처 ({filteredResults.length})
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <button type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={selectAllFiltered}
                            disabled={allFilteredSelected}
                            className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-40 disabled:cursor-not-allowed px-1.5 py-0.5 rounded hover:bg-emerald-100">
                      검색결과 전체선택
                    </button>
                    <span className="text-emerald-300">·</span>
                    <button type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clearAllFiltered}
                            className="text-[11px] font-semibold text-slate-500 hover:text-rose-600 px-1.5 py-0.5 rounded hover:bg-slate-100">
                      해제
                    </button>
                  </div>
                </div>
                {filteredResults.map((p, pi) => {
                  const checked = value.includes(p.id)
                  const navI = (showGroups ? groups.length : 0) + pi
                  const active = navI === activeIdx
                  return (
                    <button key={p.id} type="button"
                            data-picker-idx={navI}
                            onMouseEnter={() => setActiveIdx(navI)}
                            onClick={(e) => toggle(p, pi, e.shiftKey)}
                            className={cn(
                              'w-full text-left px-3 py-2 text-sm flex items-center gap-2',
                              active
                                ? 'bg-emerald-100'
                                : checked
                                  ? 'bg-emerald-50'
                                  : 'hover:bg-emerald-50',
                            )}>
                      <input type="checkbox" readOnly checked={checked} className="accent-emerald-600"/>
                      <span className="font-mono text-xs text-slate-500 tabular-nums shrink-0">{p.code}</span>
                      <span className="truncate">{p.name}</span>
                    </button>
                  )
                })}
              </>
            )}
            {!hasAny && (
              <div className="text-center text-slate-400 py-6 text-sm">
                {search ? '검색 결과 없음' : '입력하여 검색'}
              </div>
            )}
          </div>
          {(value.length > 0 || filteredResults.length > 0) && (
            <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 flex items-center">
              <span className="text-[11px] text-slate-400">Shift+클릭: 범위 선택</span>
              {value.length > 0 && (
                <button type="button"
                        onClick={() => { setOpen(false); inputRef.current?.blur() }}
                        className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 font-semibold">
                  완료 (Esc)
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
